/* eslint-disable no-underscore-dangle */

import { InvalidationPolicyCache } from '@nerdwallet/apollo-cache-policies';
import { uniqBy } from 'lodash';

const timeToLiveInHours = (hours: number) => {
  return {
    timeToLive: hours * 60 * 60 * 1000,
  };
};

const nullable = () => {
  // Create a generic field policy that allows any field to be null by default:
  return {
    read(existing = null) {
      return existing;
    },
  };
};

export default new InvalidationPolicyCache({
  typePolicies: {
    Query: {
      fields: {
        search: {
          keyArgs: ['request', ['filters', 'timerange', 'query']],
          merge(existing, incoming) {
            const records = existing ? [...existing.records] : [];
            records.push(...(incoming.records ?? []));

            const matchedRecords = existing ? [...existing.matchedRecords] : [];
            matchedRecords.push(...(incoming.matchedRecords ?? []));

            return {
              hits: incoming.hits,
              next: incoming.next,
              records,
              matchedRecords,
            };
          },
          read(existing) {
            if (!existing) {
              return {};
            }
            return {
              hits: existing.hits,
              next: existing.next,
              records: existing.records,
              matchedRecords: existing.matchedRecords,
            };
          },
        },
        listLabels: {
          keyArgs: ['request', ['tenantID', 'type', 'includeInvisible']],
          merge(existing, incoming) {
            if (existing?.paginationToken === incoming.paginationToken) {
              return existing;
            }
            return {
              ...incoming,
              labels: [...(existing?.labels ?? []), ...(incoming.labels ?? [])],
            };
          },
        },
        listDashboards: {
          keyArgs: [
            'request',
            ['tenantID', 'userID', 'filter', 'sortBy', 'sortAsc'],
          ],
          merge(existing, incoming) {
            if (!incoming?.prevCursor) {
              return incoming;
            }
            if (existing?.nextCursor === incoming?.nextCursor) {
              return existing;
            }
            return {
              ...incoming,
              dashboards: [
                ...(existing?.dashboards ?? []),
                ...(incoming.dashboards ?? []),
              ],
            };
          },
        },
        listAnalyses: {
          keyArgs: [
            'request',
            ['tenantID', 'userID', 'filter', 'sortBy', 'sortAsc'],
          ],
          merge(existing, incoming) {
            if (!incoming?.prevCursor) {
              return incoming;
            }
            if (existing?.nextCursor === incoming?.nextCursor) {
              return existing;
            }
            return {
              ...incoming,
              analyses: [
                ...(existing?.analyses ?? []),
                ...(incoming.analyses ?? []),
              ],
            };
          },
        },
        getNotifications: {
          keyArgs: [
            'request',
            ['tenantID', 'userID', 'includeViewed', 'pageSize'],
          ],
          merge(existing, incoming) {
            if (!existing?.cursor || existing?.hits !== incoming?.hits) {
              return incoming;
            }
            if (existing?.cursor === incoming?.cursor) {
              return existing;
            }
            return {
              ...incoming,
              notifications: [
                ...(existing?.notifications ?? []),
                ...(incoming?.notifications ?? []),
              ],
            };
          },
        },
        listTenants: {
          keyArgs: ['request', []],
          merge(existing, incoming, options) {
            if (existing?.paginationToken === incoming.paginationToken) {
              return existing;
            }

            if (!options?.args?.request?.paginationToken) {
              return incoming;
            }

            return {
              ...incoming,
              tenants: [
                ...(existing?.tenants ?? []),
                ...(incoming.tenants ?? []),
              ],
            };
          },
        },
        searchArtefacts: {
          keyArgs: [
            'request',
            [
              'tenantID',
              'query',
              'types',
              'statuses',
              'scopes',
              'createdByFilter',
              'filter',
            ],
          ],
          merge(existing, incoming, options) {
            if (!options?.args?.request?.cursor) {
              return incoming;
            }
            if (!existing?.cursor) {
              return incoming;
            }
            if (existing?.cursor === incoming?.cursor) {
              return existing;
            }
            return {
              ...incoming,
              artefacts: [
                ...(existing?.artefacts ?? []),
                ...(incoming?.artefacts ?? []),
              ],
            };
          },
        },
        listEQLFieldValues: {
          keyArgs: ['request', ['tenantID', 'eqlField', 'searchTerm', 'limit']],
          merge(existing, incoming) {
            if (existing?.cursor === incoming?.cursor) {
              return existing;
            }
            return {
              ...incoming,
              values: uniqBy(
                [...(existing?.values ?? []), ...(incoming.values ?? [])],
                'value',
              ),
            };
          },
        },
        listSuggestions: {
          keyArgs: [
            'request',
            ['tenantID', 'filter', 'responseSize', 'onlyHits'],
          ],
          merge(existing, incoming, options) {
            if (!options?.args?.request?.cursor) {
              return incoming;
            }
            if (existing?.cursor === incoming?.cursor) {
              return existing;
            }
            return {
              ...incoming,
              suggestions: [
                ...(existing?.suggestions ?? []),
                ...(incoming.suggestions ?? []),
              ],
            };
          },
        },
        getRCForReason: {
          keyArgs: ['request', ['tenantID', 'reasonID']],
          merge(existing, incoming, options) {
            if (!options?.args?.request?.cursor) {
              return incoming;
            }
            if (existing?.paginationToken === incoming?.paginationToken) {
              return existing;
            }
            return {
              ...incoming,
              listRCs: [
                ...(existing?.listRCs ?? []),
                ...(incoming.listRCs ?? []),
              ],
            };
          },
        },
        getHistory: {
          keyArgs: ['request', ['tenantID', 'subjectID', 'userID']],
          merge(existing, incoming, options) {
            if (!options?.args?.request?.cursor) {
              return incoming;
            }
            if (existing?.cursor === incoming?.cursor) {
              return existing;
            }
            return {
              ...incoming,
              changes: [
                ...(existing?.changes ?? []),
                ...(incoming.changes ?? []),
              ],
            };
          },
        },
      },
    },
    // apollo cache has issues with adding cached objects containing optional
    // fields. The following config fixes it.
    // https://stackoverflow.com/a/65736091
    Dashboard: {
      fields: {
        originDashboard: nullable(),
        reporting: nullable(),
        analyses: nullable(),
        editors: nullable(),
        useCase: nullable(),
      },
    },
    DashboardListItem: {
      fields: {
        analysisIDs: nullable(),
      },
    },
  },
  invalidationPolicies: {
    types: {
      GetAggregatesResponse: timeToLiveInHours(1),
      GetRecordsResponse: timeToLiveInHours(1),
      IntegrationsFormData: timeToLiveInHours(1),
      IntegrationsFormDataV2: timeToLiveInHours(1),
      ListSourceDetailsResponse: timeToLiveInHours(1),
      ListLabelsResponse: timeToLiveInHours(1),
      Analysis: timeToLiveInHours(1),
      Dashboard: timeToLiveInHours(1),
      ReportWindowMetadata: timeToLiveInHours(1),
      GetPermissionsForUserTypesResponse: timeToLiveInHours(24),
    },
  },
  // For fragment queries having unions, the apollo client needs
  // the polymorphic relationship b/w main type & union types belonging to it.
  // Ref: https://www.apollographql.com/docs/react/data/fragments/#using-fragments-with-unions-and-interfaces
  possibleTypes: {
    IndexMetadata: [
      'IndexTypeText',
      'IndexTypeConversation',
      'IndexTypeSurvey',
      'IndexTypeAudioConversation',
      'IndexTypeKeywordSummary',
    ],
    Label: ['Aspect', 'Reason', 'Sentiment', 'Cluster', 'L1'],
    IntegrationFormData: ['OAuthFormData', 'InputFormData'],
    PredictionMetadata: [
      'AspectPredictionMetadata',
      'ReasonPredictionMetadata',
      'SentimentPredictionMetadata',
    ],
  },
});
